import { useState, useCallback } from 'react';
import {
  Elements,
  PaymentElement,
  LinkAuthenticationElement,
  AddressElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button, Input, Label } from '@scio3/ui';
import { useTranslation } from 'react-i18next';
import { useStripeSetup } from '../hooks/useStripeSetup';
import { useStripeAppearance } from '../hooks/useStripeAppearance';
import { reportPaymentError } from '@/lib/telemetry/payment-telemetry';

type StripeCardFormProps = {
  /** Stripe client secret from stripeBuildElement query */
  clientSecret: string;
  /** Payment method order from stripeBuildElement query */
  paymentMethodOrder?: string[];
  /** Default email for LinkAuthenticationElement */
  defaultEmail?: string;
  /** Slot rendered inline next to Email field (e.g. "Enter Promo code" link) */
  emailInlineSlot?: React.ReactNode;
  /** Hide the email (LinkAuthenticationElement) field — use when email is managed separately */
  hideEmail?: boolean;
  /** Submit button text */
  submitText?: string;
  /** Called on successful setup/payment confirmation. May be async — the form
   *  awaits this before re-enabling the submit button so async parent work
   *  (registerPaymentMethod mutation, refetch, confirm dialog) can't be
   *  interrupted by a duplicate Save click. (#1076) */
  onSuccess: (setupIntentId: string) => Promise<void> | void;
  /** Called when billing address changes in AddressElement — use to capture address for redirect flows */
  onBillingAddressChange?: (address: Record<string, string> | null) => void;
  /** Called on error */
  onError?: (message: string) => void;
  /** Called when form submit starts (before Stripe confirmSetup) — use to show loading in external button */
  onSubmitStart?: () => void;
  /** Return URL for 3DS redirect */
  returnUrl?: string;
  /** Show submit button (false if parent handles submit) */
  showSubmitButton?: boolean;
  /** Loading state from parent */
  isLoading?: boolean;
  /** Show billing address fields (AddressElement) — needed for non-card payment methods
   *  (iDEAL, SEPA, Bancontact) so Stripe has a valid location for automatic tax calculation */
  showBillingAddress?: boolean;
  /** Default billing address name (pre-fill AddressElement) */
  defaultBillingName?: string;
};

/**
 * #2050 — the countries whose card postal code the app collects itself, because Stripe's
 * `clover`/`dahlia` trains stopped collecting them (measured table in StripePaymentForm).
 * US is absent on purpose: Stripe still renders its own inline "ZIP code" there.
 */
const POSTAL_CODE_COUNTRIES: readonly string[] = ['CA', 'GB', 'PR'];

/**
 * Inner form component — must be inside <Elements> provider.
 */
function StripePaymentForm({
  submitText,
  onSuccess,
  onError,
  returnUrl,
  showSubmitButton = true,
  isLoading = false,
  defaultEmail,
  emailInlineSlot,
  hideEmail = false,
  paymentMethodOrder,
  onSubmitStart,
  showBillingAddress = false,
  defaultBillingName,
  onBillingAddressChange,
}: Omit<StripeCardFormProps, 'clientSecret'> & {
  defaultEmail?: string;
  emailInlineSlot?: React.ReactNode;
  hideEmail?: boolean;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Track selected payment method type — AddressElement only shown for non-card methods
  // (matching scio1: card has its own Country field, non-card needs explicit billing address).
  const [paymentMethodType, setPaymentMethodType] = useState<string>('card');
  // Capture the email entered in LinkAuthenticationElement so it can be supplied to
  // confirmSetup when the PaymentElement opts the email field out (Stripe requires the
  // opted-out field in confirmParams). Seeded from defaultEmail so a user who never
  // edits it still produces a valid email.
  const [billingEmail, setBillingEmail] = useState(defaultEmail ?? '');
  // #1127 — scio1 parity (paymentComponent.js): the PaymentElement collects "Name on
  // card" for the Card tab, but for NON-card methods (iDEAL/SEPA/Bancontact) the
  // AddressElement collects the name, so the PaymentElement's name field is opted out
  // there (dynamic, per-tab) to remove the duplicate "Full name". Capture the
  // AddressElement name so it can be supplied to confirmSetup for those methods.
  const [billingName, setBillingName] = useState(defaultBillingName ?? '');
  // #2050 — Stripe's card Element collects a postal code for exactly four countries; every
  // other country has never had the field, by design (Stripe only asks where the postal code
  // is used for card verification). Measured by sweeping all 237 countries in the Element's
  // own country select, per release train:
  //
  //   train    US   CA   PR   GB      (all other 233 countries: no postal field on any train)
  //   basil    yes  yes  yes  yes
  //   clover   yes  NO   NO   NO
  //   dahlia   yes  NO   NO   NO   ← what @stripe/stripe-js@9's loadStripe injects
  //
  // So `clover` dropped CA/PR/GB, which is #2050: no postal code means no card AVS and an
  // empty `card.address_zip`. The app fills that gap for exactly those three — the
  // PaymentElement keeps Country (`country: 'auto'`) but opts the postal code out
  // (`postalCode: 'never'`), and the value below is supplied to confirmSetup.
  //
  // Deliberately NOT "every non-US country": showing a required postal field to the 233
  // countries Stripe never asks (VN, HK, AE, …) would turn a missing-field bug into a
  // cannot-save-card bug. The list lives at module scope (POSTAL_CODE_COUNTRIES).
  const [billingCountry, setBillingCountry] = useState<string | null>(null);
  const [postalCode, setPostalCode] = useState('');
  const [postalCodeError, setPostalCodeError] = useState(false);
  const ownsPostalCode =
    paymentMethodType === 'card' &&
    !!billingCountry &&
    POSTAL_CODE_COUNTRIES.includes(billingCountry);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!stripe || !elements || submitting) return;

      // #2050 — an empty postal code defeats the fix (AVS comes back `unavailable`), and
      // Stripe rejects an opted-out field that arrives blank. Block before confirmSetup.
      //
      // `onError` fires on this path for two reasons. (1) Consistency: the callers that drive
      // submit externally via `requestSubmit()` — CreateSubAccountDialog, TeamDialog — surface
      // every other Stripe failure as a toast from their `onError`, so a silent block would be
      // the odd one out. (2) Defensive: `onError` is also their documented hook for clearing a
      // pre-set loading flag, so a future caller that sets one before submitting cannot hang.
      // No caller does that today (each returns before setting it), so this is not a live-bug
      // fix — just the correct contract.
      if (ownsPostalCode && !postalCode.trim()) {
        const message = t('Zip / Postal Code is required');
        setPostalCodeError(true);
        setErrorMessage(message);
        onError?.(message);
        return;
      }
      setPostalCodeError(false);

      setSubmitting(true);
      setErrorMessage(null);
      onSubmitStart?.();

      // #1127 — wrap stripe.confirmSetup in try/catch so SDK-thrown errors
      // (e.g., IntegrationError when fields are opted-out but not provided) surface
      // as state instead of becoming an uncaught promise rejection that leaves
      // the button stuck on "Processing..." forever.
      let error: { message?: string; type?: string } | undefined;
      let setupIntent:
        | {
            status?: string;
            payment_method?: string | { id?: string } | null;
            id: string;
          }
        | undefined;
      try {
        // When the PaymentElement opts the email field out (showBillingAddress &&
        // !hideEmail — see the `fields` config in the render below), Stripe requires
        // the email in confirmParams. Without it, confirmSetup throws an
        // IntegrationError on the Stripe Link path (a Link-sourced payment method
        // doesn't carry the opted-out email, so it isn't auto-wired from the
        // LinkAuthenticationElement). Supply the captured/seed email.
        const emailOptedOut = showBillingAddress && !hideEmail;
        // #1127 — name is opted out of the PaymentElement only for NON-card methods
        // (the AddressElement collects it). Supply it to confirmSetup for those so the
        // opted-out field is always present (Card keeps its own "Name on card").
        const nameOptedOut = showBillingAddress && paymentMethodType !== 'card';
        const optedOutBillingDetails: {
          email?: string;
          name?: string;
          address?: { postal_code: string };
        } = {};
        if (emailOptedOut && billingEmail) optedOutBillingDetails.email = billingEmail;
        if (nameOptedOut && billingName) optedOutBillingDetails.name = billingName;
        // #2050 — `postalCode: 'never'` obliges us to supply the value here. Only
        // `postal_code`: `country` stays 'auto', so Stripe collects it and passing it too
        // would conflict.
        if (ownsPostalCode) optedOutBillingDetails.address = { postal_code: postalCode.trim() };
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: returnUrl ?? `${window.location.origin}/app/s/payment-intent`,
            ...(Object.keys(optedOutBillingDetails).length
              ? { payment_method_data: { billing_details: optedOutBillingDetails } }
              : {}),
          },
          redirect: 'if_required',
        });
        error = result.error;
        setupIntent = result.setupIntent;
      } catch (e) {
        reportPaymentError('payment-method', e);
        const message = e instanceof Error ? e.message : t('Payment failed. Please try again.');
        setErrorMessage(message);
        onError?.(message);
        setSubmitting(false);
        return;
      }

      if (error) {
        reportPaymentError('payment-method', error);
        const message = error.message ?? t('Payment failed. Please try again.');
        // #1314 — For Stripe validation errors (incomplete card / expiry / cvc),
        // PaymentElement already renders an inline error anchored to each
        // offending field. Surfacing the same message again as a local <p>
        // summary below the form created a confusing duplicate. Skip the
        // local render for validation_error; consumer's onError still fires
        // (typically a toast) for visibility.
        if (error.type !== 'validation_error') {
          setErrorMessage(message);
        }
        onError?.(message);
        setSubmitting(false);
        return;
      }

      // Handle both immediate success (card) and deferred processing (SEPA Debit).
      // SEPA Debit returns 'processing' — the payment method is set up asynchronously.
      if (setupIntent?.status === 'succeeded' || setupIntent?.status === 'processing') {
        // Pass the PaymentMethod ID (pm_...), not the SetupIntent ID (seti_...)
        const paymentMethodId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : (setupIntent.payment_method?.id ?? setupIntent.id);
        try {
          await onSuccess(paymentMethodId);
        } finally {
          setSubmitting(false);
        }
        return;
      }

      // For redirect-based methods (Bancontact/iDEAL): stripe.confirmSetup() normally
      // redirects the browser — this code is unreachable in the happy path. But if we
      // arrive here (Stripe rejected the combo silently, network issue, redirect blocked,
      // or unexpected status like requires_action / requires_confirmation /
      // requires_payment_method / canceled), do NOT leave the button stuck on
      // "Processing..." forever — surface the situation and let the user retry. (#1127)
      const message = setupIntent?.status
        ? t('Payment was not completed. Please try again or use a different payment method.')
        : t('Payment failed. Please try again.');
      setErrorMessage(message);
      onError?.(message);
      setSubmitting(false);
    },
    [
      stripe,
      elements,
      submitting,
      returnUrl,
      onSuccess,
      onError,
      onSubmitStart,
      t,
      billingEmail,
      billingName,
      paymentMethodType,
      ownsPostalCode,
      postalCode,
      showBillingAddress,
      hideEmail,
    ],
  );

  return (
    <form id="stripe-payment-form" onSubmit={handleSubmit} className="space-y-4">
      {/* Email + optional inline slot (e.g. Promo code) — matching scio1 two-column layout */}
      {!hideEmail && (
        <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
          <LinkAuthenticationElement
            options={defaultEmail ? { defaultValues: { email: defaultEmail } } : undefined}
            onChange={(e) => setBillingEmail(e.value.email)}
          />
          {emailInlineSlot && <div>{emailInlineSlot}</div>}
        </div>
      )}
      {/* Billing address — only shown for non-card methods (iDEAL, SEPA,
          Bancontact, US Bank Account) that need an explicit address for Stripe
          automatic tax. Card mode keeps a single PaymentElement with no extra
          AddressElement. */}
      {showBillingAddress && paymentMethodType !== 'card' && (
        <AddressElement
          onChange={(event) => {
            // #1127 — this AddressElement is the single "Full name" source for
            // non-card methods (the PaymentElement's name is opted out below);
            // capture it for confirmSetup.
            if (typeof event.value.name === 'string') setBillingName(event.value.name);
            if (event.complete) {
              onBillingAddressChange?.(event.value.address as Record<string, string>);
            }
          }}
          options={{
            mode: 'billing',
            fields: { phone: 'never' },
            ...(defaultBillingName ? { defaultValues: { name: defaultBillingName } } : {}),
          }}
        />
      )}
      <PaymentElement
        onChange={(event) => {
          setPaymentMethodType(event.value.type);
          // #2050 — Stripe reports the selected billing country here (fires on mount too).
          setBillingCountry(event.value.billingDetails?.address?.country ?? null);
        }}
        options={{
          layout: 'tabs',
          ...(paymentMethodOrder?.length ? { paymentMethodOrder } : {}),
          // #1127 — scio1 parity (paymentComponent.js change handler). Opt
          // billingDetails fields out of the PaymentElement DYNAMICALLY per tab:
          //   - email: 'never' whenever a separate LinkAuthenticationElement collects it.
          //   - name: 'never' ONLY for NON-card methods (iDEAL/SEPA/Bancontact), where
          //     the AddressElement collects the name → removes the duplicate "Full name"
          //     under Email. Card keeps its own "Name on card" (no name opt-out), so
          //     switching back to Card restores the field. scio1 resets via
          //     `paymentElement.update({ fields: {} })`; react-stripe-js re-applies these
          //     `options` whenever `paymentMethodType` changes. The opted-out name/email
          //     are also supplied to confirmSetup above as a safety net.
          // #2050 — for a CA / GB / PR card, opt the postal code out and collect it ourselves
          // (see the `ownsPostalCode` comment above). `country` stays 'auto' so Stripe keeps
          // rendering the Country select — only the postal field moves to the app.
          fields: {
            billingDetails: {
              ...(showBillingAddress && !hideEmail ? { email: 'never' as const } : {}),
              ...(showBillingAddress && paymentMethodType !== 'card'
                ? { name: 'never' as const }
                : {}),
              ...(ownsPostalCode
                ? { address: { country: 'auto' as const, postalCode: 'never' as const } }
                : {}),
            },
          },
        }}
      />
      {/* #2050 — app-rendered Postal code for CA / GB / PR cards. It cannot join Stripe's
          Country row (that row lives inside Stripe's iframe), so it sits directly below
          the Element. Hidden for US, where Stripe still renders its own "ZIP code", and on
          non-card tabs, where the AddressElement collects the full address. */}
      {ownsPostalCode && (
        <div className="space-y-1.5">
          <Label htmlFor="stripe-postal-code">{t('Postal Code')}</Label>
          <Input
            id="stripe-postal-code"
            value={postalCode}
            onChange={(e) => {
              setPostalCode(e.target.value);
              if (postalCodeError) setPostalCodeError(false);
            }}
            placeholder={t('Postal Code')}
            autoComplete="billing postal-code"
            aria-invalid={postalCodeError}
            data-testid="stripe-postal-code-input"
            className={postalCodeError ? 'border-destructive' : undefined}
          />
        </div>
      )}
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      {showSubmitButton && (
        <Button
          type="submit"
          disabled={!stripe || submitting || isLoading}
          className="w-full cursor-pointer"
        >
          {submitting ? t('Processing...') : (submitText ?? t('Save Card'))}
        </Button>
      )}
    </form>
  );
}

/**
 * Stripe card/payment form wrapped in Elements provider.
 * Reusable across PaymentMethodPage (card update) and SubscriptionWizardDialog (checkout).
 *
 * Usage:
 *   1. Call stripeBuildElement query to get clientSecret
 *   2. Pass clientSecret to this component
 *   3. Handle onSuccess with the setupIntentId
 */
export function StripeCardForm(props: StripeCardFormProps) {
  const { stripePromise } = useStripeSetup();
  const {
    clientSecret,
    paymentMethodOrder,
    defaultEmail,
    emailInlineSlot,
    hideEmail,
    showBillingAddress,
    defaultBillingName,
    onBillingAddressChange,
    ...formProps
  } = props;

  const { appearance, isDark } = useStripeAppearance();

  if (!clientSecret) return null;

  return (
    <Elements
      key={`${clientSecret}-${isDark ? 'dark' : 'light'}`}
      stripe={stripePromise}
      options={{ clientSecret, appearance }}
    >
      <StripePaymentForm
        {...formProps}
        paymentMethodOrder={paymentMethodOrder}
        defaultEmail={defaultEmail}
        emailInlineSlot={emailInlineSlot}
        hideEmail={hideEmail}
        showBillingAddress={showBillingAddress}
        defaultBillingName={defaultBillingName}
        onBillingAddressChange={onBillingAddressChange}
      />
    </Elements>
  );
}
